import React from "react";
import styled from "styled-components";
import { useTranslation, Trans } from "react-i18next";
import { ReactComponent as RecycleBin } from "../../assets/recycle-bin.svg";
import { ReactComponent as BlackBin } from "../../assets/waste-bin-tidyman.svg";
import { ReactComponent as CompostBin } from "../../assets/compostable-bin.svg";
import { ReactComponent as YardWaste } from "../../assets/yard-waste.svg";
import { ReactComponent as SpecialPrograms } from "../../assets/special-programs.svg"; // Fixed typo
import heart from "../../assets/heart-lives.svg";
import starFish from "../../assets/starfish-smile.svg";
import crossButton from "../../assets/x-button.svg";
import Button from "../Button";
import puzzleOne from "../../assets/pz-1.png";
import puzzleTwo from "../../assets/pz-2.png";

import {
  PageHeader,
  MessageBox,
  PageTitle,
  BoxMessage,
  BadgeBox,
  OrangeText,
  CloseCross,
  IconImage,
  Spacer,
} from "../MasterCss";

const HowToPlayBox = styled.div`
  background-image: linear-gradient(#21b2d3, #7abefd);
  position: fixed;
  width: 90vw;
  height: 85vh;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  overflow-x: hidden;
  overflow-y: scroll;
  display: block;
  z-index: 3000;
  border-radius: 1em;
  border: 2px solid #08345c;
  filter: drop-shadow(4px 4px 4px #08345c);
  text-align: center;
  @media (max-width: 480px) {
    padding-bottom: 5.4vh;
    padding-top: env(safe-area-inset-top);
  }
`;

const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
`;

const CenterChildren = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const PuzzleImage = styled.img`
  width: ${(props) => props.width};
  height: ${(props) => props.height};
`;

// Mapping translation keys → bin names used in binConfig
const BIN_KEY_MAP = {
  recycle: "recycling",
  nonRecycle: "general waste",
  foodWaste: "food compost",
  yardWaste: "yard waste",
  specialPrograms: "special programs",
};

const HowToPlayModal = ({ handleClose, game, activeBins }) => {
  const { t } = useTranslation();
  const svgSize = window.matchMedia("screen and (max-width: 768px)").matches
    ? "20%"
    : "8rem";
  const isWasteSortingGame = game && game.toLowerCase() === "waste-sorting";
  const isWastePuzzleGame = game && game.toLowerCase() === "waste-puzzle";
  const gameName = game ? t(`gameNames.${game.toLowerCase()}`) : "";

  // Helper: should we show this bin?
  const shouldShowBin = (binKey) => {
    if (!activeBins || activeBins.length === 0) return true; // fallback: show all
    const binName = BIN_KEY_MAP[binKey];
    return activeBins.includes(binName);
  };

  // Reusable wrapper for a single bin instruction
  const BinMessage = ({ binKey, icon: Icon, i18nKey }) => {
    if (!shouldShowBin(binKey)) return null;
    return (
      <MessageBox>
        <BadgeBox>
          <Icon width="20%" height={svgSize} />
          <BoxMessage>
            <Trans i18nKey={i18nKey} components={{ OrangeText: <OrangeText /> }} />
          </BoxMessage>
        </BadgeBox>
      </MessageBox>
    );
  };

  return (
    <>
      <HowToPlayBox>
        <HeaderWrapper>
          <CloseCross onClick={handleClose} src={crossButton} />
          <PageHeader>
            {gameName
              ? t("howToPlay.titleWithGame", { game: gameName })
              : t("howToPlay.title")}
          </PageHeader>
        </HeaderWrapper>
        <Spacer />

        {game && game !== "" ? (
          <CenterChildren>
            {isWasteSortingGame && (
              <>
                {/* Dynamic bins – only those active for the location */}
                <BinMessage
                  binKey="recycle"
                  icon={RecycleBin}
                  i18nKey="howToPlay.wasteSorting.recycle.description"
                />
                <BinMessage
                  binKey="nonRecycle"
                  icon={BlackBin}
                  i18nKey="howToPlay.wasteSorting.nonRecycle.description"
                />
                <BinMessage
                  binKey="foodWaste"
                  icon={CompostBin}
                  i18nKey="howToPlay.wasteSorting.foodWaste.description"
                />
                <BinMessage
                  binKey="yardWaste"
                  icon={YardWaste}
                  i18nKey="howToPlay.wasteSorting.yardWaste.description"
                />
                <BinMessage
                  binKey="specialPrograms"
                  icon={SpecialPrograms}
                  i18nKey="howToPlay.wasteSorting.specialPrograms.description"
                />
                {/* Lives & Starfish – always shown */}
                <MessageBox>
                  <BadgeBox>
                    <IconImage src={heart} />
                    <BoxMessage>
                      <Trans
                        i18nKey="howToPlay.wasteSorting.lives.description"
                        components={{ OrangeText: <OrangeText /> }}
                      />
                    </BoxMessage>
                  </BadgeBox>
                </MessageBox>
                <MessageBox>
                  <BadgeBox>
                    <IconImage src={starFish} />
                    <BoxMessage>
                      <Trans
                        i18nKey="howToPlay.wasteSorting.starfish.description"
                        components={{ OrangeText: <OrangeText /> }}
                      />
                    </BoxMessage>
                  </BadgeBox>
                </MessageBox>
              </>
            )}
            {isWastePuzzleGame && (
              <>
                <MessageBox>
                  <BadgeBox>
                    <PuzzleImage src={puzzleOne} width="20%" height={svgSize} />
                    <BoxMessage>
                      <Trans
                        i18nKey="howToPlay.wastePuzzle.dragDrop.description"
                        components={{ OrangeText: <OrangeText /> }}
                      />
                    </BoxMessage>
                  </BadgeBox>
                </MessageBox>
                <MessageBox>
                  <BadgeBox>
                    <PuzzleImage src={puzzleTwo} width="20%" height={svgSize} />
                    <BoxMessage>
                      <Trans
                        i18nKey="howToPlay.wastePuzzle.match.description"
                        components={{ OrangeText: <OrangeText /> }}
                      />
                    </BoxMessage>
                  </BadgeBox>
                </MessageBox>
                <MessageBox>
                  <BadgeBox>
                    <IconImage src={heart} />
                    <BoxMessage>
                      <Trans
                        i18nKey="howToPlay.wastePuzzle.lives.description"
                        components={{ OrangeText: <OrangeText /> }}
                      />
                    </BoxMessage>
                  </BadgeBox>
                </MessageBox>
                <MessageBox>
                  <BadgeBox>
                    <IconImage src={starFish} />
                    <BoxMessage>
                      <Trans
                        i18nKey="howToPlay.wastePuzzle.starfish.description"
                        components={{ OrangeText: <OrangeText /> }}
                      />
                    </BoxMessage>
                  </BadgeBox>
                </MessageBox>
              </>
            )}
          </CenterChildren>
        ) : (
          // General view (no game selected) – show all bins, regardless of activeBins
          <>
            <PageTitle>{t("gameNames.waste-sorting")}</PageTitle>
            <MessageBox>
              <BadgeBox>
                <RecycleBin width="20%" height={svgSize} />
                <BoxMessage>
                  <Trans
                    i18nKey="howToPlay.wasteSorting.recycle.description"
                    components={{ OrangeText: <OrangeText /> }}
                  />
                </BoxMessage>
              </BadgeBox>
            </MessageBox>
            <MessageBox>
              <BadgeBox>
                <BlackBin width="20%" height={svgSize} />
                <BoxMessage>
                  <Trans
                    i18nKey="howToPlay.wasteSorting.nonRecycle.description"
                    components={{ OrangeText: <OrangeText /> }}
                  />
                </BoxMessage>
              </BadgeBox>
            </MessageBox>
            <MessageBox>
              <BadgeBox>
                <CompostBin width="20%" height={svgSize} />
                <BoxMessage>
                  <Trans
                    i18nKey="howToPlay.wasteSorting.foodWaste.description"
                    components={{ OrangeText: <OrangeText /> }}
                  />
                </BoxMessage>
              </BadgeBox>
            </MessageBox>
            <MessageBox>
              <BadgeBox>
                <YardWaste width="20%" height={svgSize} />
                <BoxMessage>
                  <Trans
                    i18nKey="howToPlay.wasteSorting.yardWaste.description"
                    components={{ OrangeText: <OrangeText /> }}
                  />
                </BoxMessage>
              </BadgeBox>
            </MessageBox>
            <MessageBox>
              <BadgeBox>
                <SpecialPrograms width="20%" height={svgSize} />
                <BoxMessage>
                  <Trans
                    i18nKey="howToPlay.wasteSorting.specialPrograms.description"
                    components={{ OrangeText: <OrangeText /> }}
                  />
                </BoxMessage>
              </BadgeBox>
            </MessageBox>
            <MessageBox>
              <BadgeBox>
                <IconImage src={heart} />
                <BoxMessage>
                  <Trans
                    i18nKey="howToPlay.wasteSorting.lives.description"
                    components={{ OrangeText: <OrangeText /> }}
                  />
                </BoxMessage>
              </BadgeBox>
            </MessageBox>
            <MessageBox>
              <BadgeBox>
                <IconImage src={starFish} />
                <BoxMessage>
                  <Trans
                    i18nKey="howToPlay.wasteSorting.starfish.description"
                    components={{ OrangeText: <OrangeText /> }}
                  />
                </BoxMessage>
              </BadgeBox>
            </MessageBox>

            <PageTitle>{t("gameNames.waste-puzzle")}</PageTitle>
            <MessageBox>
              <BadgeBox>
                <PuzzleImage src={puzzleOne} width="20%" height={svgSize} />
                <BoxMessage>
                  <Trans
                    i18nKey="howToPlay.wastePuzzle.dragDrop.description"
                    components={{ OrangeText: <OrangeText /> }}
                  />
                </BoxMessage>
              </BadgeBox>
            </MessageBox>
            <MessageBox>
              <BadgeBox>
                <PuzzleImage src={puzzleTwo} width="20%" height={svgSize} />
                <BoxMessage>
                  <Trans
                    i18nKey="howToPlay.wastePuzzle.match.description"
                    components={{ OrangeText: <OrangeText /> }}
                  />
                </BoxMessage>
              </BadgeBox>
            </MessageBox>
            <MessageBox>
              <BadgeBox>
                <IconImage src={heart} />
                <BoxMessage>
                  <Trans
                    i18nKey="howToPlay.wastePuzzle.lives.description"
                    components={{ OrangeText: <OrangeText /> }}
                  />
                </BoxMessage>
              </BadgeBox>
            </MessageBox>
            <MessageBox>
              <BadgeBox>
                <IconImage src={starFish} />
                <BoxMessage>
                  <Trans
                    i18nKey="howToPlay.wastePuzzle.starfish.description"
                    components={{ OrangeText: <OrangeText /> }}
                  />
                </BoxMessage>
              </BadgeBox>
            </MessageBox>
          </>
        )}

        <Button primary handleClick={handleClose} label={t("close")} />
        <Spacer />
      </HowToPlayBox>
    </>
  );
};

export default HowToPlayModal;